import moment from 'moment-timezone';
import React from 'react';
import { queryCache } from 'react-query';
import { getEventCompletionStatusLabel, QKeys } from 'src/api/types';
import { Poppins } from 'src/common';
import { useCache } from 'src/graphql';
import { EventCompletionStatus, EventKind, ObjectKind, useUpsertEventMutation } from 'src/graphql/_generated';
import { useStateSelector } from 'src/redux';
import { useAuth } from 'src/state/auth';
import styled, { css } from 'styled-components';

const Div = styled.div`
  max-width: 100%;
  overflow: hidden;

  .marker {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }
`;

const MarkAsCompleted = styled.span<{ $loading?: boolean }>`
  text-decoration: underline;
  cursor: pointer;

  ${({ $loading }) =>
    $loading &&
    css`
      opacity: 0.5;
      pointer-events: none;
      text-decoration: none;
    `}
`;

interface CompletionStatusProps {
  status?: EventCompletionStatus | null;
  objectId: string;
  eventId: string;
  objectType?: ObjectKind | null;
  hasVersion: boolean;
}

export const CompletionStatus: React.FC<CompletionStatusProps> = ({
  status,
  objectId,
  eventId,
  objectType,
  hasVersion,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const isCompleted = status === EventCompletionStatus.Done;
  const { workspaceId } = useAuth();
  const { refetchQuery } = useCache();

  const [upsertEvent, { loading }] = useUpsertEventMutation({
    onCompleted: () => {
      queryCache.invalidateQueries([QKeys.Audit]);
      refetchQuery({ include: ['events'] });
    },
  });

  const markAsDone = () => {
    upsertEvent({
      variables: {
        workspaceId,
        eventData: [
          {
            eventType: EventKind.Action,
            objectType,
            objectId,
            completionStatus: EventCompletionStatus.Done,
            id: eventId,
            completedAt: moment().toDate().toISOString(),
          },
        ],
      },
    });
  };

  return (
    <Div>
      {isCompleted ? (
        <div className="marker" data-cy="status-completed">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
            <circle
              cx="8.01675"
              cy="8.54788"
              r="7.59934"
              transform="rotate(-0.753493 8.01675 8.54788)"
              fill={colors.statusGreen}
              stroke={colors.statusGreen}
              strokeWidth="0.628071"
            />
            <path
              d="M6.57789 10.465L4.64303 8.58037L4.0012 9.23466L6.59494 11.7611L12.0183 6.19311L11.3641 5.55587L6.57789 10.465Z"
              fill="white"
            />
          </svg>
          <Poppins px={14} css="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;">
            {getEventCompletionStatusLabel(EventCompletionStatus.Done)}
          </Poppins>
        </div>
      ) : (
        <div
          css="display: flex; align-items: center; gap: 4px; min-width: 0; overflow: hidden;"
          data-cy="status-incomplete"
        >
          <span css="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;">
            <Poppins px={14}>{getEventCompletionStatusLabel(EventCompletionStatus.Open)}</Poppins>
          </span>
          {!hasVersion && (
            <span css="white-space: nowrap; flex-shrink: 0;">
              <Poppins px={14}>
                (
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <circle
                    cx="8.01675"
                    cy="8.54788"
                    r="7.59934"
                    transform="rotate(-0.753493 8.01675 8.54788)"
                    stroke={colors.prussianBlue}
                    strokeWidth="0.628071"
                  />
                  <path
                    d="M6.57789 10.465L4.64303 8.58037L4.0012 9.23466L6.59494 11.7611L12.0183 6.19311L11.3641 5.55587L6.57789 10.465Z"
                    fill={colors.prussianBlue}
                  />
                </svg>{' '}
                <MarkAsCompleted onClick={markAsDone} $loading={loading} data-cy="mark-complete-btn">
                  Mark complete
                </MarkAsCompleted>
                )
              </Poppins>
            </span>
          )}
        </div>
      )}
    </Div>
  );
};
